import { Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "src/redux/hooks";

import { HeaderBadge } from "../Header.styled";
import LogoutModal from "./LogoutModal";
import {
  HANDOVER_SESSION_KEY,
  LOGOUT_TIMER_REFRESH,
  refreshLogoutTimer,
} from "src/generator/utils/refreshLogoutTimer";

const HANDOVER_LOGOUT_MS = 60 * 60 * 1000;

const getSessionLogoutDuration = (logoutTimeMs: number) =>
  localStorage.getItem(HANDOVER_SESSION_KEY) ? HANDOVER_LOGOUT_MS : logoutTimeMs;

const getRemainingMs = (startMs: number, sessionDurationMs: number) => {
  const remaining = startMs + sessionDurationMs - Date.now();
  if (remaining <= 0) return 0;
  return remaining - (remaining % 1000);
};

const LogoutTimer = () => {
  const [rerender, setRerender] = useState(0);
  const [timer, setTimer] = useState(0);
  const [opened, { open, close }] = useDisclosure();
  const logoutTime = useSelector(state => state.preferencesSlice.logoutTime);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (opened) return;

    const recalc = () => setRerender(prev => prev + 1);
    const resetTimerAndRecalc = () => {
      refreshLogoutTimer();
      recalc();
    };

    const sessionLogoutDuration = getSessionLogoutDuration(logoutTime);
    const startRaw = localStorage.getItem("logoutModalStart");

    if (!startRaw) {
      resetTimerAndRecalc();
      return;
    }

    const startMs = Number(startRaw);
    if (!Number.isFinite(startMs) || startMs <= 0) {
      resetTimerAndRecalc();
      return;
    }

    const time = getRemainingMs(startMs, sessionLogoutDuration);

    if (time <= 0) {
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        resetTimerAndRecalc();
        return;
      }

      setTimer(0);
      open();
      return;
    }

    hasInitializedRef.current = true;

    if (time > 1000) {
      setTimer(time);
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setTimer(prev => {
          if (prev <= 0) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            open();
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setTimer(0);
    }

    const handleLogoutTimerRefresh = () => {
      hasInitializedRef.current = false;
      recalc();
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "logoutModalStart" || e.key === HANDOVER_SESSION_KEY) {
        handleLogoutTimerRefresh();
      }
    };

    window.addEventListener("focus", recalc);
    window.addEventListener(LOGOUT_TIMER_REFRESH, handleLogoutTimerRefresh);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      if (opened) close();
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener("focus", recalc);
      window.removeEventListener(LOGOUT_TIMER_REFRESH, handleLogoutTimerRefresh);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [close, logoutTime, open, opened, rerender]);

  const getTimer = () => {
    const totalSeconds = Math.floor(timer / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    let seconds: number | string = totalSeconds - minutes * 60;
    seconds = seconds < 10 ? "0" + seconds : seconds;
    return `${minutes}:${seconds}`;
  };

  const getColor = () => {
    const secondsLeft = Math.floor(timer / 1000);
    if (secondsLeft < 60) {
      return "red.9";
    } else if (secondsLeft < 60 * 5) {
      return "yellow.9";
    } else {
      return "green.9";
    }
  };

  return (
    <>
      <Tooltip label="Session Timeout" openDelay={300}>
        <HeaderBadge
          size="lg"
          variant="light"
          color={getColor()}
          my="auto"
          style={{ userSelect: "none" }}>
          {getTimer()}
        </HeaderBadge>
      </Tooltip>
      <LogoutModal opened={opened} close={close} />
    </>
  );
};

export default LogoutTimer;
